import i18n from '@/i18n';
import {
	type RemunerationAmountObject,
	type RemunerationAmountOption,
	RemunerationPeriodicity,
	type RemunerationPeriodicityOptionDto,
	type RemunerationPeriodicityOptionUi,
} from '@/types/remuneration';

const t = (key: string): string => i18n.global.t(key) as string;

const REMUNERATION_PERIODICITY_I18N_KEYS: Record<
	RemunerationPeriodicity,
	string
> = {
	[RemunerationPeriodicity.HOURLY]: 'global.remuneration.frequencies.hourly',
	[RemunerationPeriodicity.DAILY]: 'global.remuneration.frequencies.daily',
	[RemunerationPeriodicity.WEEKLY]: 'global.remuneration.frequencies.weekly',
	[RemunerationPeriodicity.MONTHLY]: 'global.remuneration.frequencies.monthly',
	[RemunerationPeriodicity.YEARLY]: 'global.remuneration.frequencies.yearly',
};

const isRemunerationAmountObject = (
	option: RemunerationAmountOption
): option is RemunerationAmountObject => typeof option !== 'number';

const toRemunerationPeriodicity = (type: string): RemunerationPeriodicity => {
	if (
		Object.values(RemunerationPeriodicity).includes(
			type as RemunerationPeriodicity
		)
	) {
		return type as RemunerationPeriodicity;
	}

	return RemunerationPeriodicity.HOURLY;
};

const getRemunerationPeriodicityI18nKey = (type: string): string =>
	REMUNERATION_PERIODICITY_I18N_KEYS[type as RemunerationPeriodicity] ??
	'global.notDefined';

export const getAmountValue = (option: RemunerationAmountOption): number => {
	if (!isRemunerationAmountObject(option)) return option;
	return option.value ?? option.amount ?? 0;
};

export const getAmountLabel = (
	option: RemunerationAmountOption
): string | number => {
	if (!isRemunerationAmountObject(option)) return option;
	return option.label ?? option.value ?? option.amount ?? '';
};

export const getAmountId = (
	option: RemunerationAmountOption | undefined
): string | undefined => {
	if (!option || !isRemunerationAmountObject(option)) return undefined;
	if (option.id == null) return undefined;
	return String(option.id);
};

export const mapRemunerationPeriodicityOptions = (
	periodicities: RemunerationPeriodicityOptionDto[]
): RemunerationPeriodicityOptionUi[] =>
	periodicities
		.slice()
		.sort((optionA, optionB) => optionA.order - optionB.order)
		.map((apiOption) => ({
			id: apiOption.id,
			label: t(getRemunerationPeriodicityI18nKey(apiOption.type)),
			value: toRemunerationPeriodicity(apiOption.type),
			minimumValues: apiOption.minimumValues,
			maximumValues: apiOption.maximumValues,
		}));
